import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase"; // Adjust path if necessary
import React, { useEffect, useState } from "react";
import { FaTrash, FaEdit, FaLightbulb, FaThumbtack, FaPlus, FaTimes, FaClipboardList, FaShieldAlt, FaBan } from "react-icons/fa";
import {
  fetchLessonsRows,
  appendLesson,
  updateLesson,
  deleteLesson,
} from "../services/FirestoreService";

export default function Lessons({ userId, accountId }) {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add state
  const [showAdd, setShowAdd] = useState(false);
  const [newLesson, setNewLesson] = useState("");
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editing, setEditing] = useState(false);

  // Delete modal state
  const [deleteId, setDeleteId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [tradingRules, setTradingRules] = useState(""); 
  const [recoveryStrategy, setRecoveryStrategy] = useState("");
  const [lossStrategy, setLossStrategy] = useState("");
  const [editingStrategies, setEditingStrategies] = useState(false);
  const [savingStrategies, setSavingStrategies] = useState(false);

// Fetch Strategies
  useEffect(() => {
    if (!userId) return;
    const fetchStrategies = async () => {
      try {
        const prefsRef = doc(db, "users", userId, "settings", "prefs");
        const snap = await getDoc(prefsRef);
        if (snap.exists()) {
          const data = snap.data();
          setTradingRules(data.tradingRules || ""); // NEW
          setRecoveryStrategy(data.recoveryStrategy || "");
          setLossStrategy(data.lossStrategy || "");
        }
      } catch (err) {
        console.error("Error fetching strategies:", err);
      }
    };
    fetchStrategies();
  }, [userId]);

  const handleSaveStrategies = async () => {
    if (!userId) return;
    setSavingStrategies(true);
    try {
      const prefsRef = doc(db, "users", userId, "settings", "prefs");
      await setDoc(prefsRef, {
        tradingRules: tradingRules.trim(), // NEW
        recoveryStrategy: recoveryStrategy.trim(),
        lossStrategy: lossStrategy.trim()
      }, { merge: true });
      setEditingStrategies(false);
    } catch (err) {
      console.error("Error saving strategies:", err);
      alert("Failed to save strategies.");
    } finally {
      setSavingStrategies(false);
    }
  };

  // Fetch lessons whenever IDs change
  useEffect(() => {
       if (!userId) {
      setLessons([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchLessonsRows()
      .then((rows) => setLessons(rows || []))
      .catch(() => setLessons([]))
      .finally(() => setLoading(false));
  }, [userId]);

  // Add new lesson
  const handleAddLesson = async (e) => {
    e.preventDefault();
    if (!newLesson.trim() || !userId) return;
    setSaving(true);
    try {
      await appendLesson(newLesson.trim());
      const rows = await fetchLessonsRows();
      setLessons(rows || []);
      setShowAdd(false);
      setNewLesson("");
    } catch (err) {
      console.error("Error adding lesson:", err);
      alert("Failed to add lesson.");
    } finally {
      setSaving(false);
    }
  };

  // Edit lesson
  const handleEdit = async () => {
    if (!editId || !editValue.trim()) return;
    setEditing(true);
    try {
      await updateLesson(editId, editValue.trim());
      const rows = await fetchLessonsRows();
      setLessons(rows || []);
      setEditId(null);
      setEditValue("");
    } catch (err) {
      console.error("Error updating lesson:", err);
      alert("Failed to update lesson.");
    } finally {
      setEditing(false);
    }
  };


  // Delete lesson
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteLesson(deleteId);
      const rows = await fetchLessonsRows();
      setLessons(rows || []);
      setShowDelete(false);
      setDeleteId(null);
    } catch (err) {
      console.error("Error deleting lesson:", err);
      alert("Failed to delete lesson.");
    }
  };

  // Defensive text display
  const getLessonText = (l) => {
    if (!l) return "";
    if (typeof l.lesson === "string") return l.lesson;
    if (typeof l.lesson === "object" && l.lesson.lesson) return l.lesson.lesson;
    return "";
  };

  // If IDs aren’t available yet
  if (!userId) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-4 min-h-screen">
        <div className="text-gray-600 dark:text-gray-400">
          Sign in to view lessons.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800">
            <FaLightbulb className="text-2xl text-blue-100 dark:text-gray-100" />
          </div>
          <h1 className="text-3xl font-extrabold text-blue-900 dark:text-blue-200">
            Trading Plan & Lessons
          </h1>
        </div>

      </div>

      {/* Trading Strategies Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-10">
        <div className="flex justify-between items-start mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
          <div>
            <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-200">
              Core Strategies
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Define your non-negotiable rules and risk management plans.
            </p>
          </div>
          {!editingStrategies && (
            <button
              onClick={() => setEditingStrategies(true)}
              className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition"
            >
              <FaEdit /> Edit
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Trading Rules Card */}
                    <div className="bg-blue-50/50 dark:bg-blue-900/10 border-l-4 border-blue-500 rounded-r-xl p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <FaClipboardList className="text-blue-500 text-lg" />
                        <label className="font-bold text-blue-900 dark:text-blue-200 text-lg">
                          Trading Rules
                        </label>
                      </div>
                      <span className="block text-sm font-normal text-gray-500 dark:text-gray-400 mb-3">
                        Your core requirements for entering a trade, managing the trade, and exit strategy. 
                        <strong className="text-blue-600 dark:text-blue-400"> Put each rule on a new line to generate your checklist.</strong>
                      </span>
                      {editingStrategies ? (
                        <textarea
                          value={tradingRules}
                          onChange={(e) => setTradingRules(e.target.value)}
                          rows={4}
                          placeholder="1. Only trade during NY session&#10;2. Wait for candle close&#10;3. Risk 1% max"
                          className="w-full px-4 py-2 rounded-lg border border-blue-200 dark:border-blue-800 focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-800 dark:text-white transition shadow-sm"
                        />
                      ) : (
                        <p className="text-gray-700 dark:text-gray-300 font-medium whitespace-pre-wrap leading-relaxed">
                          {tradingRules || "No trading rules defined."}
                        </p>
                      )}
                    </div>

          {/* Recovery Strategy Card */}
          <div className="bg-green-50/50 dark:bg-green-900/10 border-l-4 border-green-500 rounded-r-xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <FaShieldAlt className="text-green-500 text-lg" />
              <label className="font-bold text-green-900 dark:text-green-200 text-lg">
                Recovery Strategy
              </label>
            </div>
            <span className="block text-sm font-normal text-gray-500 dark:text-gray-400 mb-3">
              Plan for a losing day.
            </span>
            {editingStrategies ? (
              <textarea
                value={recoveryStrategy}
                onChange={(e) => setRecoveryStrategy(e.target.value)}
                rows={3}
                placeholder="E.g., Stop trading for the day, review charts, go for a walk..."
                className="w-full px-4 py-2 rounded-lg border border-green-200 dark:border-green-800 focus:ring-2 focus:ring-green-400 bg-white dark:bg-gray-800 dark:text-white transition shadow-sm"
              />
            ) : (
              <p className="text-gray-700 dark:text-gray-300 font-medium whitespace-pre-wrap leading-relaxed">
                {recoveryStrategy || "No recovery strategy defined."}
              </p>
            )}
          </div>

          {/* Loss Strategy Card */}
          <div className="bg-red-50/50 dark:bg-red-900/10 border-l-4 border-red-500 rounded-r-xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <FaBan className="text-red-500 text-lg" />
              <label className="font-bold text-red-900 dark:text-red-200 text-lg">
                Loss Strategy
              </label>
            </div>
            <span className="block text-sm font-normal text-gray-500 dark:text-gray-400 mb-3">
              Plan after a specific number of losing trades.
            </span>
            {editingStrategies ? (
              <textarea
                value={lossStrategy}
                onChange={(e) => setLossStrategy(e.target.value)}
                rows={3}
                placeholder="E.g., If I lose 3 trades in a row, I will step away for 4 hours..."
                className="w-full px-4 py-2 rounded-lg border border-red-200 dark:border-red-800 focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-800 dark:text-white transition shadow-sm"
              />
            ) : (
              <p className="text-gray-700 dark:text-gray-300 font-medium whitespace-pre-wrap leading-relaxed">
                {lossStrategy || "No loss strategy defined."}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {editingStrategies && (
          <div className="mt-6 flex gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={handleSaveStrategies}
              disabled={savingStrategies}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-2.5 rounded-xl shadow transition"
            >
              {savingStrategies ? "Saving..." : "Save Strategies"}
            </button>
            <button
              onClick={() => setEditingStrategies(false)}
              disabled={savingStrategies}
              className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold px-8 py-2.5 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

          {/* Lessons Section Header */}
      <div className="flex items-center justify-between mb-2 mt-4">
        <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-200">
          Lessons Learned
        </h2>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-800 text-white font-bold px-5 py-2 rounded-xl shadow transition"
        >
          {showAdd ? "Cancel" : "Add Lesson"}
        </button>
      </div>
      <div className="mb-8 text-left text-gray-500 dark:text-gray-400 text-sm">
        Document your insights to refine your trading edge and review them daily.
      </div>


      {/* Add form */}
      {showAdd && (
        <form
          className="mb-8 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow border border-gray-200 dark:border-gray-700"
          onSubmit={handleAddLesson}
        >
          <h2 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-3">
            Add New Lesson
          </h2>
          <div className="mb-4">
            <label className="block font-semibold mb-1 text-gray-800 dark:text-gray-200">
              Lesson
            </label>
            <textarea
              required
              rows={3}
              value={newLesson}
              onChange={(e) => setNewLesson(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400 bg-gray-50 dark:bg-gray-700 dark:text-white transition"
              maxLength={500}
              placeholder="What did you learn?"
            />
          </div>
          <button
            disabled={saving}
            type="submit"
            className="bg-blue-600 dark:bg-blue-700 text-white font-bold px-7 py-2 rounded-xl shadow hover:bg-blue-700 dark:hover:bg-blue-800 transition"
          >
            {saving ? "Saving..." : "Add Lesson"}
          </button>
        </form>
      )}

      {/* Lessons list */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="p-10 text-center text-gray-400 dark:text-gray-400">
            Loading...
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {lessons.length === 0 ? (
              <li className="p-8 text-center text-gray-400 dark:text-gray-500">
                No lessons found.
              </li>
            ) : (
              lessons.map((row) => (
                <li
                  key={row.id}
                  className="p-6 flex items-start gap-4 hover:bg-blue-50/30 dark:hover:bg-blue-900/20 transition group"
                >
                  {/* Tack on the left */}
                  <FaThumbtack className="text-gray-400 dark:text-gray-600 text-xl mt-1 transform -rotate-12" />

                  <div className="flex-1">
                    {editId === row.id ? (
                      <>
                        <textarea
                          className="w-full px-3 py-2 rounded bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 dark:text-white"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          rows={3}
                          maxLength={500}
                        />
                        <div className="mt-2 flex gap-2">
                          <button
                            className="px-4 py-1 rounded bg-blue-600 text-white font-bold text-xs hover:bg-blue-700"
                            disabled={editing}
                            onClick={handleEdit}
                          >
                            Save
                          </button>
                          <button
                            className="px-4 py-1 rounded bg-gray-200 text-gray-700 font-bold text-xs hover:bg-gray-300"
                            disabled={editing}
                            onClick={() => setEditId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <span className="font-semibold text-blue-800 dark:text-blue-200 text-lg">
                        {getLessonText(row) || "(No lesson)"}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-end gap-2">
                    {editId !== row.id && (
                      <button
                        onClick={() => {
                          setEditId(row.id);
                          setEditValue(getLessonText(row) || "");
                        }}
                        className="p-1 rounded-full text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 transition"
                        title="Edit"
                      >
                        <FaEdit size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setDeleteId(row.id);
                        setShowDelete(true);
                      }}
                      className="p-1 rounded-full text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition"
                      title="Delete"
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {/* Delete Confirmation Modal (styled like Delete Account) */}
      {showDelete && deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-8 py-8 min-w-[340px] max-w-[95vw] border-2 border-red-400 text-center">
            <div className="mb-4">
              <FaTrash className="text-3xl text-red-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">
                Delete Lesson
              </h2>
              <div className="text-gray-700 dark:text-gray-300">
                Are you sure you want to delete this lesson?
              </div>
              <div className="mt-3 px-3 py-2 rounded bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-blue-900 dark:text-blue-200 text-base font-semibold">
                {getLessonText(lessons.find((l) => l.id === deleteId))}
              </div>
            </div>
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => {
                  setShowDelete(false);
                  setDeleteId(null);
                }}
                className="px-6 py-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-bold hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

